import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, Empty, Collapse, Modal, Tooltip } from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { NetworkLayer } from '../../../services/common';
import { NetworkLayer as TmNetworkLayer } from '../../../services/tmCommon';
import {
  ExportHistoryWrapper,
  ExportHistoryHeader,
  ExportHistoryTitle,
  ExportHistoryTitleIcon,
  ExportHistoryItem,
  ExportHistoryItemHeader,
  ExportHistoryItemTitle,
  ExportHistoryItemStatus,
  ExportHistoryItemMeta,
  ExportHistoryItemActions,
  FiltersAppliedContent,
  FilterItem,
  FilterLabel,
  FilterValue,
  EmptyStateWrapper,
  ExportHistoryBadge,
  EllipsisText
} from './exportHistoryStyled';
import { capitalize, startCase } from 'lodash';

const { Panel } = Collapse;

// Modules where isTriggeredOnly is not applicable and should be hidden when false
const MODULES_WITHOUT_TRIGGERED_ONLY = [
  'CRS_TRANSACTION_HISTORY',
  'CM_CRS_TRANSACTION_HISTORY',
  'CM_CASE_CRS_TRANSACTION_HISTORY',
  'CM_MANUAL_CUSTOMER_TRANSACTION_HISTORY'
];

const STATUS_CONFIG = {
  completed: { color: '#52c41a', icon: <CheckCircleOutlined /> },
  inprogress: { color: '#1890ff', icon: <ClockCircleOutlined /> },
  failed: { color: '#ff4d4f', icon: <CloseCircleOutlined /> }
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  return moment(timestamp).format('DD/MM/YYYY HH:mm');
};

const truncateAlertId = (alertId, maxLen = 16) => {
  if (!alertId) return '';
  return alertId.length > maxLen
    ? `${alertId.substring(0, maxLen)}...`
    : alertId;
};

const commonService = new NetworkLayer();
const tmService = new TmNetworkLayer();

const ExportHistory = ({
  alertId,
  caseId,
  partyId,
  isActive,
  isCaseLevel = false,
  title = 'Export History for this Alert',
  module,
  isManualCase = false,
  refreshKey
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [typologyMap, setTypologyMap] = useState({});

  const fetchExportHistory = useCallback(async () => {
    if (!alertId && !caseId && !partyId) return;
    setIsLoading(true);
    try {
      const params = { module };
      if (alertId) params.alertId = alertId;
      if (caseId) params.caseId = caseId;
      if (partyId) params.partyId = partyId;

      const response = await commonService.amlsExportHistoryAPI(params);
      setData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch export history:', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [alertId, caseId, partyId, module]);

  useEffect(() => {
    if (isActive) {
      fetchExportHistory();
    }
  }, [isActive, refreshKey, fetchExportHistory]);

  useEffect(() => {
    if (!alertId) return;
    tmService
      .tmCommonTransactionTriggeredTypologiesAPI({ alertId })
      .then((res) => {
        if (Array.isArray(res)) {
          const map = {};
          res.forEach(({ id, name }) => {
            map[id] = name;
          });
          setTypologyMap(map);
        }
      })
      .catch(() => {});
  }, [alertId]);

  const handleDownload = useCallback(async (jobId) => {
    setDownloadingIds((prev) => [...prev, jobId]);
    try {
      const response = await commonService.amlsDownloadUrlAPI(jobId);
      if (response?.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to fetch download URL:', error);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== jobId));
    }
  }, []);

  const deleteExportHistoryItem = useCallback(
    async (id) => {
      setIsLoading(true);
      try {
        await commonService.amlsDeleteExportHistoryAPI(id);
        await fetchExportHistory();
      } catch (error) {
        console.error('Failed to delete export history item:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchExportHistory]
  );

  const handleDelete = useCallback(
    (id) => {
      Modal.confirm({
        title: 'Delete Export',
        content: 'Are you sure you want to delete this export?',
        okText: 'Delete',
        okType: 'danger',
        onOk: () => {
          deleteExportHistoryItem(id);
        }
      });
    },
    [deleteExportHistoryItem]
  );

  const parseFilters = (filterParam) => {
    try {
      const {
        alertId: _a,
        customerId: _c,
        caseId: _cs,
        minAmount: _mi,
        maxAmount: _ma,
        download: _d,
        isCM: _isCM,
        moduleId: _moduleId,
        module: _module,
        from,
        to,
        ...rest
      } = JSON.parse(atob(filterParam || ''));
      const filters = {};
      if (from && to) {
        filters['Date Range'] = `${moment(from).format(
          'DD/MM/YYYY'
        )} to ${moment(to).format('DD/MM/YYYY')}`;
      }
      return {
        ...filters,
        isTriggeredOnly: rest.isTriggeredOnly ?? false,
        ...rest
      };
    } catch {
      return {};
    }
  };

  const buildTitle = useCallback(
    (item, filters) => {
      const alertId = truncateAlertId(item.alertId);
      const dateRange = filters?.['Date Range'];

      if (isCaseLevel && caseId) {
        return `Case ${caseId} - All Transactions`;
      }

      if (dateRange) {
        return `Alert ${alertId} from ${dateRange}`;
      }

      return `Alert ${alertId}`;
    },
    [isCaseLevel, caseId]
  );

  return (
    <ExportHistoryWrapper>
      <Spin spinning={isLoading}>
        <ExportHistoryHeader>
          <ExportHistoryTitle>
            <ExportHistoryTitleIcon>
              <FileTextOutlined />
            </ExportHistoryTitleIcon>
            {title}
            <ExportHistoryBadge count={data.length} />
          </ExportHistoryTitle>
        </ExportHistoryHeader>

        {data.length === 0 ? (
          <EmptyStateWrapper>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No export history found"
            />
          </EmptyStateWrapper>
        ) : (
          data.map((item) => {
            const status = item.status || '';
            const normalizedStatus = status.toLowerCase();
            const statusConfig =
              STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.inprogress;
            const isCompleted = normalizedStatus === 'completed';
            const parsedFilters = parseFilters(item.filterParam);
            const isFailedStatus = normalizedStatus === 'failed';
            const tooltipTitle = isFailedStatus ? item.errorMessage : null;

            return (
              <ExportHistoryItem key={item.jobId}>
                <ExportHistoryItemHeader>
                  <ExportHistoryItemTitle>
                    {buildTitle(item, parsedFilters)}
                  </ExportHistoryItemTitle>
                  <Tooltip title={tooltipTitle} placement="top">
                    <ExportHistoryItemStatus $color={statusConfig.color}>
                      {statusConfig.icon} {capitalize(status)}
                    </ExportHistoryItemStatus>
                  </Tooltip>
                </ExportHistoryItemHeader>

                <ExportHistoryItemMeta>
                  <span>{formatTimestamp(item.createdAt)}</span>
                  <span>&bull;</span>
                  <span>{item.recordCount} records</span>
                </ExportHistoryItemMeta>
                {item.alertId && (
                  <ExportHistoryItemMeta>
                    <Tooltip title={item.alertId}>
                      <EllipsisText>Alert: {item.alertId}</EllipsisText>
                    </Tooltip>
                  </ExportHistoryItemMeta>
                )}
                {item.caseId && (
                  <ExportHistoryItemMeta>
                    <span>Case: {item.caseId}</span>
                  </ExportHistoryItemMeta>
                )}

                {Object.keys(parsedFilters).length > 0 && (
                  <Collapse
                    ghost
                    expandIconPosition="start"
                    style={{ marginTop: 4 }}>
                    <Panel
                      header={
                        <span style={{ fontSize: 13, color: '#556069' }}>
                          <FilterOutlined style={{ marginRight: 4 }} />
                          Filters Applied
                        </span>
                      }
                      key="filters">
                      <FiltersAppliedContent>
                        {Object.entries(parsedFilters).map(([key, value]) => {
                          if (
                            key === 'isTriggeredOnly' &&
                            !value &&
                            (MODULES_WITHOUT_TRIGGERED_ONLY.includes(module) ||
                              isManualCase)
                          )
                            return null;
                          return (
                            <FilterItem key={key}>
                              <FilterLabel>
                                {key === 'typologyId'
                                  ? 'Typology'
                                  : key === 'isTriggeredOnly'
                                  ? 'Triggered Only'
                                  : key === 'accountId'
                                  ? 'Account Number'
                                  : key === 'transactionType'
                                  ? 'Transaction Type'
                                  : startCase(key)}:
                              </FilterLabel>
                              <FilterValue>
                                {key === 'typologyId'
                                  ? typologyMap[value] || value
                                  : String(value)}
                              </FilterValue>
                            </FilterItem>
                          );
                        })}
                      </FiltersAppliedContent>
                    </Panel>
                  </Collapse>
                )}

                <ExportHistoryItemActions>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={downloadingIds.includes(item.jobId)}
                    onClick={() => handleDownload(item.jobId)}
                    disabled={!isCompleted}>
                    Download
                  </Button>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(item.jobId)}
                  />
                </ExportHistoryItemActions>
              </ExportHistoryItem>
            );
          })
        )}
      </Spin>
    </ExportHistoryWrapper>
  );
};

ExportHistory.propTypes = {
  alertId: PropTypes.string,
  caseId: PropTypes.string,
  partyId: PropTypes.string,
  isActive: PropTypes.bool,
  isCaseLevel: PropTypes.bool,
  title: PropTypes.string,
  isManualCase: PropTypes.bool,
  refreshKey: PropTypes.number
};

export default ExportHistory;
